import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Package } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const statusColor = {
  paid: "text-[#EAB308] border-[#EAB308]",
  pending: "text-zinc-400 border-zinc-600",
  shipped: "text-[#FF3B30] border-[#FF3B30]",
  delivered: "text-green-400 border-green-400",
  cancelled: "text-red-500 border-red-500",
};

export default function Account() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-[1000px] mx-auto px-6 py-12" data-testid="account-page">
      <h1 className="font-display text-5xl uppercase tracking-tighter">My <span className="text-[#FF3B30]">Account</span></h1>
      <p className="text-zinc-400 mt-2 mb-10">{user?.name} · {user?.email}</p>

      <h2 className="font-display text-2xl uppercase tracking-tight mb-6">Order History</h2>
      {loading ? (
        <div className="text-zinc-500 py-12">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="border border-zinc-800 bg-zinc-900 p-12 text-center">
          <Package className="mx-auto text-zinc-700 mb-4" size={48} />
          <p className="text-zinc-400">No orders yet.</p>
          <Link to="/shop" className="mt-6 inline-block bg-[#FF3B30] text-white font-bold uppercase tracking-widest text-sm px-8 py-3 hover:bg-white hover:text-black transition-colors duration-200">Start Shopping</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => (
            <div key={o.id} className="border border-zinc-800 bg-zinc-900 p-5" data-testid={`order-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="text-xs text-zinc-500 uppercase tracking-widest">#{o.id.slice(-8)} · {new Date(o.created_at).toLocaleDateString()}</div>
                <span className={`text-xs font-bold uppercase tracking-widest border px-3 py-1 ${statusColor[o.status] || "text-zinc-400 border-zinc-600"}`}>{o.status}</span>
              </div>
              <div className="flex flex-wrap gap-3">
                {o.items?.map((it, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm">
                    <img src={it.image} alt={it.name} className="h-12 w-12 object-cover border border-zinc-800" />
                    <span className="text-zinc-300">{it.name} <span className="text-zinc-500">×{it.quantity}</span></span>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-zinc-800">
                <span className="text-xs uppercase tracking-widest text-zinc-500">Total</span>
                <span className="font-display text-xl text-[#FF3B30]">${o.total?.toFixed(2)}</span>
              </div>
              {o.tracking_url && (
                <a href={o.tracking_url} target="_blank" rel="noreferrer" data-testid={`track-${o.id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#EAB308] hover:underline">
                  Track Shipment →
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
